// @flow
import type { Action } from "redux-action";
import type { SimplifiedShiftPattern } from "@modules/shared/reducer/employee-absences/types";
import { differenceInCalendarDays, isWeekend } from "date-fns";
import { isBankHoliday } from "@modules/shared/utils/public-holidays";
import { getWeekDay, formatDateToInputFormat } from "@transformers/date";
import * as actions from "@modules/recording/constants/actions";
import {
  UpdateEmployeeDetailsSuccess,
  UpdateEmployeeDetails,
  UpdateEmployeeDetailsError,
} from "@modules/employee/constants/actions";
import triage from "./triage";
import type { RecordingState } from "./types";
import type { AbsenceTimeLostType, ReferralType } from "@modules/recording/types";
import { initialState } from "./state";
import { cloneObject } from "@utils/object";
import { startRequest, finishRequest } from "@store/utils";
import { getRepeatAbsenceShiftPattern } from "@modules/shared/utils/repeat-absence-pattern";

export const setLoading = (state: RecordingState) => () => startRequest(state);

export const setLoaded = (state: RecordingState) => () => finishRequest(state);

// Core Config

export const onSetClient = (state: RecordingState) => ({ payload }: Object) => {
  state.client = payload;
  return state;
};

export const onSetSettings = (state: RecordingState) => ({ payload }: Object) => {
  state.settings = payload;
  return state;
};

export const onSetEmployee = (state: RecordingState) => ({ payload }: Object) => {
  state.employee = payload;
  setLoaded(state)();
  return state;
};

export const onSetEmployeeSchema = (state: RecordingState) => ({ payload }: Object) => {
  state.schemas.employee;
  return state;
};

export const onSetWillingToTriage = (state: RecordingState) => ({ payload }: Object) => {
  state.willingToTriage = payload;
  return state;
};

export const onSetInteractionType = (state: RecordingState) => ({ payload }: Object) => {
  state.interactionType = payload;
  if (payload === "MEDICAL_TRIAGE") return state;

  // check this later, was making a startRequest
  return state;
};

export const onSetKeepOpenOverride = (state: RecordingState) => ({ payload }: Object) => {
  state.closeAbsenceOverride = payload;
  return state;
};

export const onSetCallInteractionAbsence = (state: RecordingState) => ({ payload }: Object) => {
  state.selectedAbsence = payload;
  return state;
};

export const onSetAutoIdData = (state: RecordingState) => ({ payload }: Object) => {
  state.autoIdData = payload;
  return state;
};

export const onSetCallPurposeSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.callPurpose = payload;
  return finishRequest(state);
};

export const onSetClientSettings = (state: RecordingState) => ({ payload }: Object) => {
  state.clientSettings = payload;
  return state;
};

export const onSetAutoIdDataPrefilled = (state: RecordingState) => ({ payload }: Object) => {
  state.autoIdDataPrefilled = payload;
  return state;
};

// Recording Config

export const onGetAbsenceRecordingConfigSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.absenceRecordingConfig = payload;
  return finishRequest(state);
};

export const onGetAbsenceRecordingConfigError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Call Interaction

export const onCreateCallInteractionSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.callInteraction = payload;
  return finishRequest(state);
};

export const onCreateCallInteractionError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onUpdateCallInteractionSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.callInteraction = {
    ...payload,
    interactionId: state.callInteraction ? state.callInteraction.id : payload.id,
    id: state.callInteraction ? state.callInteraction.id : payload.id,
  };
  return finishRequest(state);
};

export const onUpdateCallInteractionError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onConfirmCallInteractionError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Absence

export const onCloseAbsenceSuccess = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    isAbsenceClosed: true,
  };
};

export const onAbsenceSubmitSuccess = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    isAbsenceDetailsSubmitted: true,
    callInteraction: {
      ...state.callInteraction,
      reasonAndDuration: {
        ...(state.callInteraction?.reasonAndDuration || {}),
        isCloseable: payload.reasonAndDuration.isCloseable,
      },
    },
  };
};

export const onCloseAbsenceError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Nurse Transfer

export const onCreateNurseTransferOfferSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return { ...state, nurseTransferOffer: payload };
};

export const onNurseTransferOfferReset = (state: RecordingState) => ({ payload }: Object) => {
  return { ...state, nurseTransferOffer: null };
};

export const onGetNurseTransferOfferSchemaSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.schemas.nurseTransferOfferSchema = payload;
  return finishRequest(state);
};

export const onGetNurseTransferOfferSchemaError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onSetNurseTransferDecision = (state: RecordingState) => ({ payload }: Object) => {
  state.nurseTransferDecision = payload;
  return finishRequest(state);
};

export const onGetAvailableNurseAdvisors = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    error: null,
    nurseTransfer: { ...state.nurseTransfer, selected: null, loading: true },
  };
};

export const onGetAvailableNurseAdvisorsSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    nurseTransfer: { ...state.nurseTransfer, results: payload, loading: false },
  };
};

export const onGetAvailableNurseAdvisorsError = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
    nurseTransfer: { ...state.nurseTransfer, results: null, loading: false },
  };
};

export const onSetSelectedNurseAdvisor = (state: RecordingState) => ({ payload }: Object) => ({
  ...state,
  nurseTransfer: { ...state.nurseTransfer, selected: payload },
});

export const onAssignEmployeeToNurseAdvisor = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    error: null,
    nurseTransfer: {
      ...state.nurseTransfer,
      response: null,
      assignmentError: false,
      loading: true,
    },
  };
};

export const onAssignEmployeeToNurseAdvisorSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    nurseTransfer: { ...state.nurseTransfer, response: payload, loading: false },
  };
};

export const onAssignEmployeeToNurseAdvisorError = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
    nurseTransfer: { ...state.nurseTransfer, assignmentError: true, loading: false },
  };
};

export const onDeassignEmployeeToNurseAdvisor = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: null,
    nurseTransfer: {
      ...state.nurseTransfer,
      loading: true,
    },
  };
};

export const onDeassignEmployeeToNurseAdvisorSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    nurseTransfer: { ...state.nurseTransfer, response: null, loading: false },
  };
};

export const onDeassignEmployeeToNurseAdvisorError = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
    nurseTransfer: { ...state.nurseTransfer, assignmentError: true, loading: false },
  };
};

export const onGetIncomingNurseTransfer = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    nurseTransferQueue: { ...state.nurseTransferQueue, error: false },
  };
};

export const onGetIncomingNurseTransferSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    nurseTransferQueue: { ...state.nurseTransferQueue, data: payload },
  };
};

export const onGetIncomingNurseTransferError = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    nurseTransferQueue: { ...state.nurseTransferQueue, error: true },
  };
};

export const onAcceptNurseTransfer = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    acceptedTransfer: payload.transfer,
  };
};

// Callbacks

export const onGetCallbackQueuesSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.callbackQueues = payload;
  return finishRequest(state);
};

export const onGetCallbackQueuesError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onSelectNextCallbackSuccess = (state: RecordingState) => ({ payload }: Object) => {
  finishRequest(state);
  state.modals.nextCallback.visible = true;
  state.modals.nextCallback.callback = payload;
  return finishRequest(state);
};

export const onSelectNextCallbackError = (state: RecordingState) => ({ payload }: Object) => {
  state.modals.nextCallback.visible = false;
  state.modals.nextCallback.callback = null;
  state.error = payload;
  return finishRequest(state);
};

export const onUpdateCallbackSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.modals.nextCallback.visible = false;
  state.modals.nextCallback.callback = payload;
  return finishRequest(state);
};

export const onUpdateCallbackError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onAcceptNurseCallback = (state: RecordingState) => ({ payload }: Object) => {
  state.acceptedCallback = payload;

  return startRequest(state);
};

export const onGetCallbackScriptSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.callbackScript = payload;
  return finishRequest(state);
};

export const onGetCallbackScriptError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onCloseNextCallbackModal = (state: RecordingState) => ({ payload }: Object) => {
  state.modals.nextCallback.visible = false;
  state.modals.nextCallback.callback = null;
  return state;
};

// Employee

export const onSearchEmployees = (state: RecordingState) => ({ payload }: Object) => {
  state.employeeSearch.filters = payload.filters;
  return startRequest(state);
};

export const onSearchEmployeesSuccess = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    employeeSearch:
      payload.pagination.pageNumber === 1
        ? payload
        : {
            ...state.employeeSearch,
            content: [...state.employeeSearch.content, ...payload.content],
          },
  };
};

export const onSearchEmployeesError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onGetEmployeeSchemaSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.schemas.employee = payload;
  return finishRequest(state);
};

export const onGetEmployeeSchemaError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onGetEmployeeAbsenceRecordingSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    error: null,
    employeeAbsenceRecording: {
      ...state.employeeAbsenceRecording,
      ...payload,
    },
  };
};
export const onGetEmployeeAbsenceRecordingWellbeingServiceSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    error: null,
    employeeAbsenceRecording: {
      ...state.employeeAbsenceRecording,
      eapDetails: payload,
    },
  };
};

export const onGetEmployeeAbsenceRecordingWellbeingServiceError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onUpdateRecordingAccessLevelSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    employeeAbsenceRecording: {
      ...state.employeeAbsenceRecording,
      goodShapeApp: {
        ...state.employeeAbsenceRecording.goodShapeApp,
        userType: payload.userType,
      },
    },
  };
};

export const onGetEmployeeAbsenceRecordingError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onUpdateEmployeeDetailsData = (state: RecordingState) => ({ payload }: Object) => {
  const newWizardState: Array<Object> = state.wizardState.map((item, index) => {
    if (index === 3) {
      return {
        ...item,
        employee: {
          ...item.employee,
          securityQuestion: payload,
        },
      };
    }
    return item;
  });
  return { ...state, wizardState: newWizardState };
};

export const onUpdateEmployeeDetailsSuccess = (state: RecordingState) => ({ payload }: Object) => {
  // for some reason, someone decide to store the same data on 2 locations
  // what is this?
  if (state.wizardState && state.wizardState[3]) {
    state.wizardState[3].employee = payload;
  }
  state.employee = payload;
  return {
    ...finishRequest(state),
    employeeAbsenceRecording: {
      ...state.employeeAbsenceRecording,
      employee: payload,
    },
  };
};

export const onUpdateEmployeeDetailsError = (state: RecordingState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  error: payload,
});

// Absence Landing

export const onAssignAbsenceLandingError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onCreateReasonAndDurationError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onGetAbsenceLandingSchemaSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.schemas.absenceLanding = payload;
  return finishRequest(state);
};

export const onGetAbsenceLandingSchemaError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Reason and Duration

export const onGetAbsenceReasonAndDurationSchemaSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.schemas.reasonAndDuration = payload;
  return finishRequest(state);
};

export const onSetReasonAndDurationFormData = (state: RecordingState) => ({ payload }: Object) => {
  state.reasonAndDurationFormData = payload;
  return state;
};

export const onSetReasonAndDuration = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    reasonAndDuration: payload,
    callInteraction: {
      ...state.callInteraction,
      reasonAndDuration: payload,
    },
  };
};

export const onGetAbsenceReasonAndDurationSchemaError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Shift Pattern

export const onGetEmployeePositionsSuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.shiftPatternAnswers.positionsLoaded = true;
  state.employeePositions = payload;
  return finishRequest(state);
};

export const onGetEmployeePositionsError = (state: RecordingState) => ({ payload }: Object) => {
  state.shiftPatternAnswers.positionsLoaded = true;
  state.error = payload;
  return finishRequest(state);
};

export const onSetShiftPatternAnswers = (state: RecordingState) => ({
  payload,
}: {
  payload: Object,
}) => {
  state.shiftPatternAnswers = payload;
  return state;
};

export type GenerateTimeLostPatternRequest = $Exact<{
  assignedPositions: ?Array<Object>,
  recordedTimeLost: ?Array<Object>,
}>;

export const onGenerateTimeLostPattern = (state: RecordingState) => ({ payload }: Object) => {
  if (!payload) {
    return state;
  }

  const { recordedTimeLost, assignedPositions, captureAbsenceBankHolidays, bankHolidays } = payload;
  const { callInteraction, shiftPatternAnswers, reasonAndDuration } = state;
  const { absenceStartsAt, rtwAt } = reasonAndDuration || {};
  const { selectedPatterns = [] } = shiftPatternAnswers;

  const startDate = new Date(absenceStartsAt);
  const numberOfDays = differenceInCalendarDays(new Date(rtwAt), startDate) + 1;

  const lastRecordedDay = (recordedTimeLost || []).reduce(
    (max: string, atl: AbsenceTimeLostType) => (atl.recordedDay > max ? atl.recordedDay : max),
    "",
  );

  const nextTimeLost = numberOfDays
    ? [...new Array(numberOfDays)].map((_: *, i: number) => {
        startDate.setDate(startDate.getDate() + (i === 0 ? 0 : 1));
        const recordedDay = formatDateToInputFormat(startDate);
        const current = recordedTimeLost
          ? recordedTimeLost.find((atl: AbsenceTimeLostType) => atl.recordedDay === recordedDay)
          : null;
        const isPublicHolidayDisabled =
          !captureAbsenceBankHolidays && isBankHoliday(recordedDay, bankHolidays);

        // The last recorded day is the previous RTW day, possibly saved with zero hours lost,
        // so we don't reuse it so it gets regenerated from the shift pattern below
        const isPreviousRtwDay =
          recordedDay === lastRecordedDay && current?.timeLostInSeconds === 0;
        if (current && !isPreviousRtwDay) {
          return current;
        }

        const day = getWeekDay(startDate);
        const activeShiftPatterns = (selectedPatterns || []).filter(sp =>
          (sp.shiftPatternDays || []).find(d => day === d.day && d.isWorkingDay),
        );

        const firstShiftPattern = activeShiftPatterns[0];

        const assignedPattern = firstShiftPattern
          ? firstShiftPattern.shiftPatternDays.find(d => day === d.day)
          : null;

        const assignedPosition = assignedPositions
          ? assignedPositions.find(ap => ap.assignedPatternId === (firstShiftPattern || {}).id) ||
            assignedPositions[0]
          : null;

        // If assigned pattern exists use it to determine working day type,
        // otherwise default to week / weekend days
        let dayTypeId = 1;
        if (assignedPattern) {
          dayTypeId = assignedPattern.isWorkingDay ? 1 : 2;
        } else {
          dayTypeId = isWeekend(startDate) ? 2 : 1;
        }
        if (isPublicHolidayDisabled) {
          dayTypeId = 2;
        }

        // If assigned pattern exists pre-populate with shift patterns hours,
        // unless it's the return to work day in which case always override to 0
        const isRtwDay = i === numberOfDays - 1;

        const timeLostInSeconds =
          assignedPattern && assignedPattern.isWorkingDay && !isPublicHolidayDisabled
            ? assignedPattern?.shiftDurationSec
            : 0;

        return {
          recordedDay,
          dayType: { id: dayTypeId },
          employeePosition: assignedPosition
            ? {
                id: assignedPosition.positionId,
              }
            : null,
          workingTimeInSeconds: timeLostInSeconds,
          timeLostInSeconds: isRtwDay || isPublicHolidayDisabled ? 0 : timeLostInSeconds,
        };
      })
    : [];

  state.shiftPatternAnswers.timeLost = nextTimeLost;
  return state;
};

export type RepeatTimeLostPatternRequest = $Exact<{
  shiftPattern: Array<SimplifiedShiftPattern>,
  publicHolidayDates: string[],
  daysN: number,
}>;

export const onRepeatTimeLostPattern = (state: RecordingState) => ({
  payload,
}: Action<RepeatTimeLostPatternRequest>) => {
  if (!payload) {
    return state;
  }

  const newShiftPattern = getRepeatAbsenceShiftPattern(payload);
  state.shiftPatternAnswers.timeLost = newShiftPattern;
  return state;
};

export const onSetUpdateShiftPatternTimeLost = (state: RecordingState) => ({ payload }: Object) => {
  state.updateTimeLost = payload;
  return state;
};

export const onGetAbsenceTimeLostSchemaSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.schemas.timeLost = payload;
  return finishRequest(state);
};

export const onGetAbsenceTimeLostSchemaError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onAssignAbsenceTimeLostError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Additional Questions

export const onGetCallInteractionStandardQuestionsSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.standardQuestions = payload;
  return finishRequest(state);
};

export const onGetCallInteractionStandardQuestionsError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

export const onGetCallInteractionDynamicQuestionsSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.dynamicQuestions = payload;
  return finishRequest(state);
};

export const onGetCallInteractionReferralQuestionsSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    referrals: {
      ...state.referrals,
      list: payload,
    },
  };
};

export const onSetCallInteractionReferralQuestionsValid = (state: RecordingState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    referrals: {
      ...state.referrals,
      isValid: payload,
    },
  };
};
export const onPutCallInteractionReferralQuestionsSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  const referrals = state.referrals.list.map((ref: ReferralType) => {
    if (ref.id === payload.id) return payload;
    return ref;
  });
  return {
    ...finishRequest(state),
    referrals: {
      list: referrals,
      ...state.referrals,
    },
  };
};

export const onGetCallInteractionDynamicQuestionsError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Log Query

export const onCreateQuerySuccess = (state: RecordingState) => ({ payload }: Object) => {
  state.error = null;
  state.query = payload;
  return finishRequest(state);
};

export const onCreateQueryError = (state: RecordingState) => ({ payload }: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Interaction Notes

export const onGetInteractionNotesSchemaSuccess = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.schemas.interactionNotes = payload;
  return finishRequest(state);
};

export const onGetInteractionNotesSchemaError = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.error = payload;
  return finishRequest(state);
};

// Employee Sidebar

export const onToggleEmployeeRecordSidebar = (state: RecordingState) => ({ payload }: Object) => {
  state.isEmployeeRecordSidebarExpanded = payload;
  return state;
};

export const onSetEmployeeRecordNotifications = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.employeeRecordNotifications = payload;
  return state;
};

export const onToggleEmployeeAbsenceInteractionsModal = (state: RecordingState) => ({
  payload,
}: Object) => {
  state.modals.absenceInteractions = payload;
  return state;
};

// Modals

export const onCloseAbsenceModalToggle = (state: RecordingState) => ({ payload }: Object) => {
  state.modals.closeAbsenceModal.visible = payload;
  return state;
};

export const onToggleIncomingCallModal = (state: RecordingState) => ({ payload }: Object) => {
  state.wizardState = [];
  state.defaultWizardStep = 2;
  state.modals.incomingCall.visible = payload;
  return state;
};

export const onToggleNurseTransferModal = (state: RecordingState) => ({ payload }: Object) => {
  state.wizardState = [];
  state.defaultWizardStep = 1;
  state.modals.nurseTransfer.visible = payload;
  return state;
};

export const onToggleNurseCallbackModal = (state: RecordingState) => ({ payload }: Object) => {
  state.wizardState = [];
  state.defaultWizardStep = 1;
  state.modals.nurseCallback.visible = payload;
  return state;
};

// Reset

export const onResetAbsenceRecording = (state: RecordingState) => (action: *) => {
  return { ...initialState };
};

const onUpdateWizardState = (state: RecordingState) => ({ payload }: Object) => {
  return {
    ...state,
    // Reset selected employee if current step is before employee selection
    employee: payload.length >= 4 ? state.employee : null,
    submitCurrentStep: false,
    currentStepData: null,
    wizardState: payload,
  };
};
const onSetDefaultWizardStep = (state: RecordingState) => ({ payload }: Object) => {
  state.defaultWizardStep = payload;
  return state;
};

const onSubmitCurrentStep = (state: RecordingState) => ({ payload }: Object) => {
  state.submitCurrentStep = true;
  state.currentStepData = payload;
  return state;
};

// Triage

const onTriage = (state: RecordingState) => (action: any) => {
  const result = triage(state.triage, action);
  if (state.triage !== result) {
    return {
      ...state,
      triage: result,
    };
  }
  return state;
};

const onGetMedicalTriageHistorySuccess = (state: RecordingState) => ({ payload }): Object => {
  return {
    ...finishRequest(state),
    medicalTriageHistory: payload,
  };
};

const onGetMedicalTriageDetailsSuccess = (state: RecordingState) => ({ payload }): Object => {
  return {
    ...finishRequest(state),
    medicalTriageDetails: payload,
  };
};

const onGetClientInformation = (state: RecordingState) => (action: any) => ({
  ...state,
  requestsInProgress: state.requestsInProgress - 1,
  clientInformation: {
    ...state.clientInformation,
    list: action.payload.content,
    sort: ((action.payload.pagination || {}).sort || {}).orders,
    pagination: action.payload.pagination || {},
    totalElements: action.payload.totalElements,
  },
});

const onGetJobDutiesSuccess = (state: RecordingState) => ({ payload }): Object => {
  return {
    ...finishRequest(state),
    jobDuties: payload,
  };
};

const onGetQuestionsAndAnswersSuccess = (state: RecordingState) => ({ payload }): Object => {
  return {
    ...finishRequest(state),
    questionsAnswers: payload,
  };
};

export default (state: RecordingState = initialState, action: *): RecordingState => {
  const act = {
    [actions.SelectClient]: setLoading,
    [actions.SelectClientSuccess]: setLoaded,
    [actions.SetClient]: onSetClient,
    [actions.SetSettings]: onSetSettings,
    [actions.SetEmployee]: onSetEmployee,
    [actions.SetEmployeeSchema]: onSetEmployeeSchema,
    [actions.SetWillingToTriage]: onSetWillingToTriage,
    [actions.SetInteractionType]: onSetInteractionType,
    [actions.SetCloseAbsenceOverride]: onSetKeepOpenOverride,
    [actions.SetCallInteractionAbsence]: onSetCallInteractionAbsence,
    [actions.SetAutoIdData]: onSetAutoIdData,
    [actions.SetCallPurpose]: setLoading,
    [actions.SetCallPurposeSuccess]: onSetCallPurposeSuccess,
    [actions.SetClientSettings]: onSetClientSettings,
    [actions.SetAutoIdDataPrefilled]: onSetAutoIdDataPrefilled,
    // Recording Config
    [actions.GetAbsenceRecordingConfig]: setLoading,
    [actions.GetAbsenceRecordingConfigSuccess]: onGetAbsenceRecordingConfigSuccess,
    [actions.GetAbsenceRecordingConfigError]: onGetAbsenceRecordingConfigError,

    // Call Interaction
    [actions.CreateCallInteraction]: setLoading,
    [actions.CreateCallInteractionSuccess]: onCreateCallInteractionSuccess,
    [actions.CreateCallInteractionError]: onCreateCallInteractionError,
    [actions.GetCallInteraction]: setLoading,
    [actions.GetCallInteractionSuccess]: onUpdateCallInteractionSuccess,
    [actions.GetCallInteractionError]: onUpdateCallInteractionError,

    [actions.UpdateCallInteraction]: setLoading,
    [actions.UpdateCallInteractionSuccess]: onUpdateCallInteractionSuccess,
    [actions.UpdateCallInteractionError]: onUpdateCallInteractionError,

    [actions.ConfirmCallInteraction]: setLoading,
    [actions.ConfirmCallInteractionSuccess]: onUpdateCallInteractionSuccess,
    [actions.ConfirmCallInteractionError]: onConfirmCallInteractionError,

    // Absence
    [actions.CloseAbsence]: setLoading,
    [actions.CloseAbsenceSuccess]: onCloseAbsenceSuccess,
    [actions.CloseAbsenceError]: onCloseAbsenceError,
    [actions.CreateNewAbsence]: setLoading,
    [actions.CreateNewAbsenceSuccess]: onAbsenceSubmitSuccess,
    [actions.CreateNewAbsenceError]: setLoaded,
    [actions.UpdateExistingAbsence]: setLoading,
    [actions.UpdateExistingAbsenceSuccess]: onAbsenceSubmitSuccess,
    [actions.UpdateExistingAbsenceError]: setLoaded,

    // Nurse Transfer
    [actions.GetNurseTransferOfferSchema]: setLoading,
    [actions.CreateNurseTransferOfferSuccess]: onCreateNurseTransferOfferSuccess,
    [actions.GetNurseTransferOfferSchemaSuccess]: onGetNurseTransferOfferSchemaSuccess,
    [actions.GetNurseTransferOfferSchemaError]: onGetNurseTransferOfferSchemaError,
    [actions.SetNurseTransferDecision]: onSetNurseTransferDecision,
    [actions.GetAvailableNurseAdvisors]: onGetAvailableNurseAdvisors,
    [actions.GetAvailableNurseAdvisorsSuccess]: onGetAvailableNurseAdvisorsSuccess,
    [actions.GetAvailableNurseAdvisorsError]: onGetAvailableNurseAdvisorsError,
    [actions.SetSelectedNurseAdvisor]: onSetSelectedNurseAdvisor,
    [actions.AssignEmployeeToNurseAdvisor]: onAssignEmployeeToNurseAdvisor,
    [actions.AssignEmployeeToNurseAdvisorSuccess]: onAssignEmployeeToNurseAdvisorSuccess,
    [actions.AssignEmployeeToNurseAdvisorError]: onAssignEmployeeToNurseAdvisorError,
    [actions.DeassignEmployeeToNurseAdvisor]: onDeassignEmployeeToNurseAdvisor,
    [actions.DeassignEmployeeToNurseAdvisorSuccess]: onDeassignEmployeeToNurseAdvisorSuccess,
    [actions.DeassignEmployeeToNurseAdvisorError]: onDeassignEmployeeToNurseAdvisorError,
    [actions.GetIncomingNurseTransfer]: onGetIncomingNurseTransfer,
    [actions.GetIncomingNurseTransferSuccess]: onGetIncomingNurseTransferSuccess,
    [actions.GetIncomingNurseTransferError]: onGetIncomingNurseTransferError,
    [actions.AcceptNurseTransfer]: onAcceptNurseTransfer,
    [actions.AcceptNurseTransferSuccess]: onUpdateCallInteractionSuccess,
    [actions.AssignTransferLandingSuccess]: onUpdateCallInteractionSuccess,

    // Callback
    [actions.GetCallbackQueues]: setLoading,
    [actions.GetCallbackQueuesSuccess]: onGetCallbackQueuesSuccess,
    [actions.GetCallbackQueuesError]: onGetCallbackQueuesError,

    [actions.SelectNextCallback]: setLoading,
    [actions.SelectNextCallbackSuccess]: onSelectNextCallbackSuccess,
    [actions.SelectNextCallbackError]: onSelectNextCallbackError,

    [actions.UpdateCallback]: setLoading,
    [actions.UpdateCallbackSuccess]: onUpdateCallbackSuccess,
    [actions.UpdateCallbackError]: onUpdateCallbackError,

    [actions.AcceptNurseCallback]: onAcceptNurseCallback,
    [actions.AcceptNurseCallbackSuccess]: onUpdateCallInteractionSuccess,
    [actions.AssignCallbackLandingSuccess]: onUpdateCallInteractionSuccess,

    [actions.GetCallbackScript]: setLoading,
    [actions.GetCallbackScriptSuccess]: onGetCallbackScriptSuccess,
    [actions.GetCallbackScriptError]: onGetCallbackScriptError,

    [actions.CloseNextCallbackModal]: onCloseNextCallbackModal,

    // Employee
    [actions.SearchEmployees]: onSearchEmployees,
    [actions.SearchEmployeesSuccess]: onSearchEmployeesSuccess,
    [actions.SearchEmployeesError]: onSearchEmployeesError,

    [actions.GetEmployeeSchema]: setLoading,
    [actions.GetEmployeeSchemaSuccess]: onGetEmployeeSchemaSuccess,
    [actions.GetEmployeeSchemaError]: onGetEmployeeSchemaError,

    [actions.GetEmployeeAbsenceRecording]: setLoading,
    [actions.GetEmployeeAbsenceRecordingSuccess]: onGetEmployeeAbsenceRecordingSuccess,
    [actions.GetEmployeeAbsenceRecordingError]: onGetEmployeeAbsenceRecordingError,

    [actions.GetEmployeeAbsenceRecordingWellbeingService]: setLoading,
    [actions.GetEmployeeAbsenceRecordingWellbeingServiceSuccess]: onGetEmployeeAbsenceRecordingWellbeingServiceSuccess,
    [actions.GetEmployeeAbsenceRecordingWellbeingServiceError]: onGetEmployeeAbsenceRecordingWellbeingServiceError,
    [actions.UpdateRecordingAccessLevelSuccess]: onUpdateRecordingAccessLevelSuccess,

    [actions.UpdateEmployeeDetailsData]: onUpdateEmployeeDetailsData,
    [UpdateEmployeeDetails]: setLoading,
    [UpdateEmployeeDetailsSuccess]: onUpdateEmployeeDetailsSuccess,
    [UpdateEmployeeDetailsError]: onUpdateEmployeeDetailsError,

    // Absence Landing
    [actions.AssignAbsenceLanding]: setLoading,
    [actions.AssignAbsenceLandingSuccess]: onUpdateCallInteractionSuccess,
    [actions.AssignAbsenceLandingError]: onAssignAbsenceLandingError,

    // [actions.GetAbsenceLandingSchema]: setLoading,
    [actions.GetAbsenceLandingSchemaSuccess]: onGetAbsenceLandingSchemaSuccess,
    [actions.GetAbsenceLandingSchemaError]: onGetAbsenceLandingSchemaError,

    // Reason and Duration
    [actions.CreateReasonAndDuration]: setLoading,
    [actions.CreateReasonAndDurationSuccess]: onUpdateCallInteractionSuccess,
    [actions.CreateReasonAndDurationError]: onCreateReasonAndDurationError,
    [actions.GetAbsenceReasonAndDurationSchema]: setLoading,
    [actions.GetAbsenceReasonAndDurationSchemaSuccess]: onGetAbsenceReasonAndDurationSchemaSuccess,
    [actions.GetAbsenceReasonAndDurationSchemaError]: onGetAbsenceReasonAndDurationSchemaError,
    [actions.SetReasonAndDurationFormData]: onSetReasonAndDurationFormData,
    [actions.SetReasonAndDuration]: onSetReasonAndDuration,

    // Shift Patterns
    [actions.GetEmployeePositions]: setLoading,
    [actions.GetEmployeePositionsSuccess]: onGetEmployeePositionsSuccess,
    [actions.GetEmployeePositionsError]: onGetEmployeePositionsError,

    [actions.SetShiftPatternAnswers]: onSetShiftPatternAnswers,
    [actions.GenerateTimeLostPattern]: onGenerateTimeLostPattern,
    [actions.RepeatTimeLostPattern]: onRepeatTimeLostPattern,
    [actions.SetUpdateShiftPatternTimeLost]: onSetUpdateShiftPatternTimeLost,

    [actions.GetAbsenceTimeLostSchema]: setLoading,
    [actions.GetAbsenceTimeLostSchemaSuccess]: onGetAbsenceTimeLostSchemaSuccess,
    [actions.GetAbsenceTimeLostSchemaError]: onGetAbsenceTimeLostSchemaError,

    [actions.AssignAbsenceTimeLost]: setLoading,
    [actions.AssignAbsenceTimeLostSuccess]: onUpdateCallInteractionSuccess,
    [actions.AssignAbsenceTimeLostError]: onAssignAbsenceTimeLostError,

    // Additional Questions
    [actions.GetCallInteractionStandardQuestionsSuccess]: onGetCallInteractionStandardQuestionsSuccess,
    [actions.GetCallInteractionStandardQuestionsError]: onGetCallInteractionStandardQuestionsError,
    [actions.GetCallInteractionDynamicQuestionsSuccess]: onGetCallInteractionDynamicQuestionsSuccess,
    [actions.GetCallInteractionReferralQuestionsSuccess]: onGetCallInteractionReferralQuestionsSuccess,
    [actions.SetCallInteractionReferralQuestionsValid]: onSetCallInteractionReferralQuestionsValid,
    [actions.PutCallInteractionReferralQuestionsSuccess]: onPutCallInteractionReferralQuestionsSuccess,
    [actions.GetCallInteractionDynamicQuestionsError]: onGetCallInteractionDynamicQuestionsError,
    [actions.AssignStandardQuestionAnswersSuccess]: onUpdateCallInteractionSuccess,
    [actions.AssignDynamicQuestionAnswersSuccess]: onUpdateCallInteractionSuccess,

    // Log Query
    [actions.CreateQuery]: setLoading,
    [actions.CreateQuerySuccess]: onCreateQuerySuccess,
    [actions.CreateQueryError]: onCreateQueryError,

    // Interaction Notes
    [actions.GetInteractionNotesSchema]: setLoading,
    [actions.GetInteractionNotesSchemaSuccess]: onGetInteractionNotesSchemaSuccess,
    [actions.GetInteractionNotesSchemaError]: onGetInteractionNotesSchemaError,

    // Employee Sidebar
    [actions.ToggleEmployeeRecordSidebar]: onToggleEmployeeRecordSidebar,
    [actions.SetEmployeeRecordNotifications]: onSetEmployeeRecordNotifications,
    [actions.ToggleEmployeeAbsenceInteractionsModal]: onToggleEmployeeAbsenceInteractionsModal,

    // Modals
    [actions.CloseAbsenceModalToggle]: onCloseAbsenceModalToggle,
    [actions.ToggleIncomingCallModal]: onToggleIncomingCallModal,
    [actions.ToggleNurseTransferModal]: onToggleNurseTransferModal,
    [actions.ToggleNurseCallbackModal]: onToggleNurseCallbackModal,

    // Reset
    [actions.ResetAbsenceRecording]: onResetAbsenceRecording,

    [actions.SetAbsenceRecordingWizardState]: onUpdateWizardState,
    [actions.SetDefaultWizardStep]: onSetDefaultWizardStep,
    [actions.SubmitCurrentStep]: onSubmitCurrentStep,

    [actions.RestoreCallback]: setLoading,
    [actions.AssignCallInteractionNotes]: setLoading,
    [actions.AssignCallInteractionNotesSuccess]: setLoaded,

    // Medical Triage History
    [actions.GetMedicalTriageHistory]: setLoading,
    [actions.GetMedicalTriageHistorySuccess]: onGetMedicalTriageHistorySuccess,
    [actions.GetMedicalTriageHistoryError]: setLoaded,
    [actions.GetMedicalTriageDetails]: setLoading,
    [actions.GetMedicalTriageDetailsSuccess]: onGetMedicalTriageDetailsSuccess,
    [actions.GetMedicalTriageDetailsError]: setLoaded,

    // Job Duties
    [actions.GetJobDuties]: setLoading,
    [actions.GetJobDutiesSuccess]: onGetJobDutiesSuccess,
    [actions.GetJobDutiesError]: setLoaded,

    // Questions
    [actions.GetQuestionsAndAnswers]: setLoading,
    [actions.GetQuestionsAndAnswersSuccess]: onGetQuestionsAndAnswersSuccess,
    [actions.GetQuestionsAndAnswersError]: setLoaded,

    // TODO: the following api call may take more than 10 seconds to finish
    // and it is not the main feature on the page so its loader disabled for now for better user experience
    [actions.GetClientInformation]: setLoading,
    [actions.GetClientInformationError]: setLoaded,
    [actions.GetClientInformationSuccess]: onGetClientInformation,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return onTriage(state)(action);
};
